<!-- The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work. -->

<template>
  <div class="min-h-screen bg-white">
    <!-- 公共导航栏 -->
    <NavBar />

    <!-- Hero 区域 -->
    <div class="relative pt-24 overflow-hidden">
      <div class="absolute inset-0 z-0">
        <img 
          :src="heroImage" 
          alt="关于我们" 
          class="w-full h-full object-cover object-top"
        />
      </div>
      <div class="relative z-10 container mx-auto px-4 py-16 md:py-32 opacity-0">
        <div class="max-w-2xl bg-white/80 backdrop-blur-sm p-10 rounded-lg">
          <h1 class="text-4xl md:text-5xl font-bold text-gray-800 mb-4">玲珑AI定制</h1>
          <p class="text-xl text-gray-700 mb-6">让科技赋能个性生活</p>
          <button class="bg-lime-500 hover:bg-lime-600 text-white px-8 py-3 rounded-full font-medium transition duration-300 cursor-pointer !rounded-button whitespace-nowrap">
            了解更多
          </button>
        </div>
      </div>
    </div>

    <!-- 公司简介 -->
    <section class="py-10 md:py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row items-center gap-6 md:gap-12">
          <div class="w-full md:w-1/2 overflow-hidden rounded-xl">
            <img 
              :src="aboutImage" 
              alt="公司简介" 
              class="w-full h-40 md:h-full object-cover object-top"
            />
          </div>
          <div class="w-full md:w-1/2">
            <h2 class="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6 text-center">我们的故事</h2>
            <p class="text-gray-600 mb-4">玲珑AI定制是一家以创新为核心驱动力的人工智能科技公司，致力于通过前沿AI技术重塑消费品的个性化体验。我们专注于构建全球领先的C端在线定制平台，为用户提供覆盖服装、鞋帽、家居用品（如保温杯、法兰绒毯、丝巾）等多元品类的"一键式"智能设计服务，让每个人都能轻松成为自己生活的设计师。</p>
            <p class="text-gray-600 mb-4">依托生成式AI、计算机视觉与深度学习算法，我们突破了传统定制行业的高门槛与长周期瓶颈。用户仅需上传灵感图片、输入文字描述或选择风格模板，系统即可在数秒内生成多套专属设计方案，并支持实时3D预览与细节微调。无论是将家庭照片转化为印花丝巾，还是将手绘涂鸦变成潮流丝巾图案，玲珑AI以技术之力让天马行空的创意即刻落地。</p>
            <p class="text-gray-600 mb-8">我们深知个性化需求背后的情感价值，因此构建了从设计到生产的全链路保障体系：平台聚合全球优质供应链资源，采用环保柔性生产技术，确保每件商品兼具个性表达与实用品质。同时，智能推荐引擎会根据用户行为数据持续优化设计建议，让定制体验越用越"懂你"。</p>
            <div class="flex flex-wrap gap-4">
              <div class="flex items-center">
                <div class="bg-lime-100 p-3 rounded-full mr-3">
                  <i class="el-icon-star-on text-lime-600 text-xl"></i>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800">500万+</h3>
                  <p class="text-sm text-gray-600">注册用户</p>
                </div>
              </div>
              <div class="flex items-center">
                <div class="bg-lime-100 p-3 rounded-full mr-3">
                  <i class="el-icon-shopping-bag-2 text-lime-600 text-xl"></i>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800">10万+</h3>
                  <p class="text-sm text-gray-600">优质商品</p>
                </div>
              </div>
              <div class="flex items-center">
                <div class="bg-lime-100 p-3 rounded-full mr-3">
                  <i class="el-icon-location text-lime-600 text-xl"></i>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800">300+</h3>
                  <p class="text-sm text-gray-600">覆盖城市</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心优势 -->
    <section class="py-10 md:py-20">
      <div class="container mx-auto px-4">
        <div class="text-center mb-8 md:mb-16">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-800 mb-2 md:mb-4">我们的核心优势</h2>
          <p class="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">玲珑ai定制商城始终坚持以客户为中心，不断提升品质和服务水平，为您打造卓越的购物体验</p>
        </div>
        
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          <div class="bg-white p-4 md:p-8 rounded-xl shadow-sm border border-lime-200 hover:shadow-md transition duration-300">
            <div class="bg-lime-100 p-3 md:p-4 rounded-full inline-block mb-4 md:mb-6">
              <i class="el-icon-goods text-lime-600 text-2xl"></i>
            </div>
            <h3 class="text-xl font-semibold text-gray-800 mb-3">严选优质商品</h3>
            <p class="text-gray-600">我们与全球数千家优质供应商合作，严格把控商品品质，确保每一件商品都经过严格筛选和质检。</p>
          </div>
          
          <div class="bg-white p-4 md:p-8 rounded-xl shadow-sm border border-lime-200 hover:shadow-md transition duration-300">
            <div class="bg-lime-100 p-3 md:p-4 rounded-full inline-block mb-4 md:mb-6">
              <i class="el-icon-price-tag text-lime-600 text-2xl"></i>
            </div>
            <h3 class="text-xl font-semibold text-gray-800 mb-3">透明公正定价</h3>
            <p class="text-gray-600">我们坚持透明定价原则，杜绝虚假促销，让消费者以合理的价格购买到高品质的商品。</p>
          </div>
          
          <div class="bg-white p-4 md:p-8 rounded-xl shadow-sm border border-lime-200 hover:shadow-md transition duration-300">
            <div class="bg-lime-100 p-3 md:p-4 rounded-full inline-block mb-4 md:mb-6">
              <i class="el-icon-truck text-lime-600 text-2xl"></i>
            </div>
            <h3 class="text-xl font-semibold text-gray-800 mb-3">高效物流配送</h3>
            <p class="text-gray-600">自建物流体系与第三方物流深度合作，确保商品快速安全送达，提供全程跟踪服务。</p>
          </div>
          
          <div class="bg-white p-4 md:p-8 rounded-xl shadow-sm border border-lime-200 hover:shadow-md transition duration-300">
            <div class="bg-lime-100 p-3 md:p-4 rounded-full inline-block mb-4 md:mb-6">
              <i class="el-icon-service text-lime-600 text-2xl"></i>
            </div>
            <h3 class="text-xl font-semibold text-gray-800 mb-3">贴心客户服务</h3>
            <p class="text-gray-600">7×24小时专业客服团队，为您提供购前咨询、购中指导和购后服务，解决您的任何疑问。</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 发展历程 -->
    <section class="py-10 md:py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-8 md:mb-16">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-800 mb-2 md:mb-4">玲珑AI定制发展历程</h2>
          <p class="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">回顾玲珑AI定制的成长足迹，见证我们不断创新与进步的每一步</p>
        </div>
        
        <div class="relative">
          <!-- 时间轴线 -->
          <div class="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-lime-200"></div>
          
          <div class="grid grid-cols-1 gap-6 md:gap-12">
            <div class="flex flex-col md:flex-row items-center">
              <div class="md:w-1/2 md:pr-12 md:text-right order-2 md:order-1">
                <h3 class="text-xl font-semibold text-gray-800 mb-2">2018年 - 技术筑基，开启图像探索</h3>
                <p class="text-gray-600 mb-4">公司正式成立，以计算机视觉技术为核心，聚焦OpenCV图像处理算法的研发与应用，为传统制造业提供智能化设计解决方案，奠定AI驱动的个性化技术基础。</p>
              </div>
              <div class="flex justify-center items-center mb-6 md:mb-0 order-1 md:order-2">
                <div class="bg-lime-600 rounded-full h-10 w-10 flex items-center justify-center z-10">
                  <span class="text-white font-bold">1</span>
                </div>
              </div>
              <div class="md:w-1/2 md:pl-12 order-3">
                <div class="rounded-xl overflow-hidden">
                  <img 
                    :src="timelineImage1" 
                    alt="创立之初" 
                    class="w-full h-32 md:h-48 object-cover object-top"
                  />
                </div>
              </div>
            </div>
            
            <div class="flex flex-col md:flex-row items-center">
              <div class="md:w-1/2 md:pr-12 order-3 md:order-1">
                <div class="rounded-xl overflow-hidden">
                  <img 
                    :src="timelineImage2" 
                    alt="业务扩张" 
                    class="w-full h-32 md:h-48 object-cover object-top"
                  />
                </div>
              </div>
              <div class="flex justify-center items-center mb-6 md:mb-0 order-1 md:order-2">
                <div class="bg-lime-600 rounded-full h-10 w-10 flex items-center justify-center z-10">
                  <span class="text-white font-bold">2</span>
                </div>
              </div>
              <div class="md:w-1/2 md:pl-12 md:text-left order-2 md:order-3">
                <h3 class="text-xl font-semibold text-gray-800 mb-2">2023年3月 - 战略升级，拥抱大模型时代</h3>
                <p class="text-gray-600 mb-4">全面转型生成式AI与大模型研发，攻克多模态图像生成技术，实现从"功能化工具"向"创意赋能平台"的跨越，开启AI定制化商业模式的雏形。</p>
              </div>
            </div>
            
            <div class="flex flex-col md:flex-row items-center">
              <div class="md:w-1/2 md:pr-12 md:text-right order-2 md:order-1">
                <h3 class="text-xl font-semibold text-gray-800 mb-2">2024年3月 - 技术破界，艺术与科技共融</h3>
                <p class="text-gray-600 mb-4">自主研发的"玲珑精绘"专利技术上线，突破高精度纹理与色彩还原瓶颈，推动AI设计从"可用"迈向"惊艳"。同期，全球首场AI生成艺术画展《数字灵光》于北京798艺术区开幕，吸引超10万人次参观，引爆行业对AI创意价值的关注。</p>
              </div>
              <div class="flex justify-center items-center mb-6 md:mb-0 order-1 md:order-2">
                <div class="bg-lime-600 rounded-full h-10 w-10 flex items-center justify-center z-10">
                  <span class="text-white font-bold">3</span>
                </div>
              </div>
              <div class="md:w-1/2 md:pl-12 order-3">
                <div class="rounded-xl overflow-hidden">
                  <img 
                    :src="timelineImage3" 
                    alt="技术升级" 
                    class="w-full h-32 md:h-48 object-cover object-top"
                  />
                </div>
              </div>
            </div>
            
            <div class="flex flex-col md:flex-row items-center">
              <div class="md:w-1/2 md:pr-12 order-3 md:order-1">
                <div class="rounded-xl overflow-hidden">
                  <img 
                    :src="timelineImage4" 
                    alt="全国布局" 
                    class="w-full h-32 md:h-48 object-cover object-top"
                  />
                </div>
              </div>
              <div class="flex justify-center items-center mb-6 md:mb-0 order-1 md:order-2">
                <div class="bg-lime-600 rounded-full h-10 w-10 flex items-center justify-center z-10">
                  <span class="text-white font-bold">4</span>
                </div>
              </div>
              <div class="md:w-1/2 md:pl-12 md:text-left order-2 md:order-3">
                <h3 class="text-xl font-semibold text-gray-800 mb-2">2024年10月 - 产品落地，定义个性消费新场景</h3>
                <p class="text-gray-600 mb-4">首个AI定制单品"玲珑随行保温杯"正式量产，用户可通过AI生成专属图案并一键下单。产品首月销量突破1000件，验证C端市场对"科技+情感"消费模式的强烈需求。</p>
              </div>
            </div>
            
            <div class="flex flex-col md:flex-row items-center">
              <div class="md:w-1/2 md:pr-12 md:text-right order-2 md:order-1">
                <h3 class="text-xl font-semibold text-gray-800 mb-2">2025年5月 - 生态成型，开启全民定制时代</h3>
                <p class="text-gray-600 mb-4">"玲珑AI定制"一站式平台全球上线，整合服装、鞋帽、家居等全品类柔性供应链，支持AI设计、3D交互、智能推荐与极速生产。上线首周用户超5万，标志着个性化智造从概念迈向规模化落地。</p>
              </div>
              <div class="flex justify-center items-center mb-6 md:mb-0 order-1 md:order-2">
                <div class="bg-lime-600 rounded-full h-10 w-10 flex items-center justify-center z-10">
                  <span class="text-white font-bold">5</span>
                </div>
              </div>
              <div class="md:w-1/2 md:pl-12 order-3">
                <div class="rounded-xl overflow-hidden">
                  <img 
                    :src="timelineImage5" 
                    alt="现在" 
                    class="w-full h-32 md:h-48 object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 团队介绍 -->
    <section class="py-10 md:py-20">
      <div class="container mx-auto px-4">
        <div class="text-center mb-8 md:mb-16">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-800 mb-2 md:mb-4">我们的团队</h2>
          <p class="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">优秀的团队是玲珑ai定制商城成功的基石，我们的每一位成员都致力于为客户创造价值</p>
        </div>
        
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          <div v-for="(member, index) in teamMembers" :key="index" class="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition duration-300">
            <div class="h-40 md:h-64 overflow-hidden">
              <img 
                :src="member.image" 
                :alt="member.name" 
                class="w-full h-full object-cover object-top"
              />
            </div>
            <div class="p-4 md:p-6">
              <h3 class="text-lg md:text-xl font-semibold text-gray-800 mb-1">{{ member.name }}</h3>
              <p class="text-lime-600 mb-2 md:mb-3">{{ member.position }}</p>
              <p class="text-gray-600 text-xs md:text-sm">{{ member.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系我们 -->
    <section class="py-10 md:py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="flex flex-col lg:flex-row gap-6 md:gap-12">
          <div class="w-full lg:w-1/2">
            <h2 class="text-3xl font-bold text-gray-800 mb-6">联系我们</h2>
            <p class="text-gray-600 mb-8">无论您有任何问题、建议或合作意向，都欢迎随时与我们联系。我们的团队将竭诚为您服务。</p>
            <div class="space-y-6">
              <div class="flex items-start">
                <div class="bg-lime-100 p-3 rounded-full mr-4">
                  <i class="el-icon-location text-lime-600 text-xl"></i>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800 mb-1">公司地址</h3>
                  <p class="text-gray-600">北京市朝阳区裕民路12号1号楼11层</p>
                </div>
              </div>
              <div class="flex items-start">
                <div class="bg-lime-100 p-3 rounded-full mr-4">
                  <i class="el-icon-phone text-lime-600 text-xl"></i>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800 mb-1">联系电话</h3>
                  <p class="text-gray-600">13810005010（周一至周日 9:00-21:00）</p>
                </div>
              </div>
              <div class="flex items-start">
                <div class="bg-lime-100 p-3 rounded-full mr-4">
                  <i class="el-icon-message text-lime-600 text-xl"></i>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800 mb-1">电子邮箱</h3>
                  <p class="text-gray-600">168787363@qq.com</p>
                </div>
              </div>
              <div class="flex items-start">
                <div class="bg-lime-100 p-3 rounded-full mr-4">
                  <i class="el-icon-share text-lime-600 text-xl"></i>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-800 mb-1">社交媒体</h3>
                </div>
              </div>
            </div>
          </div>
          
          <div class="w-full lg:w-1/2">
            <div class="bg-white p-4 md:p-8 rounded-xl shadow-sm">
              <h3 class="text-lg md:text-xl font-semibold text-gray-800 mb-4 md:mb-6">给我们留言</h3>
              <form class="space-y-6">
                <div>
                  <label class="block text-gray-700 mb-2">您的姓名</label>
                  <el-input v-model="contactForm.name" placeholder="请输入您的姓名" />
                </div>
                <div>
                  <label class="block text-gray-700 mb-2">电子邮箱</label>
                  <el-input v-model="contactForm.email" placeholder="请输入您的电子邮箱" />
                </div>
                <div>
                  <label class="block text-gray-700 mb-2">留言主题</label>
                  <el-select v-model="contactForm.subject" placeholder="请选择留言主题" style="width: 100%;">
                    <el-option label="产品咨询" value="product" />
                    <el-option label="售后服务" value="service" />
                    <el-option label="投诉建议" value="complaint" />
                    <el-option label="合作洽谈" value="cooperation" />
                    <el-option label="其他" value="other" />
                  </el-select>
                </div>
                <div>
                  <label class="block text-gray-700 mb-2">留言内容</label>
                  <el-input v-model="contactForm.message" type="textarea" :rows="4" placeholder="请输入您的留言内容" />
                </div>
                <el-button type="primary" style="width: 100%;background-color:rgb(118,185,0)" @click.prevent="submitForm">提交留言</el-button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 公共页脚 -->
    <FooterBar />
  </div>
</template>

<script>
import NavBar from '@/components/common/NavBar.vue'
import FooterBar from '@/components/common/FooterBar.vue'
export default {
  components: {
    NavBar,
    FooterBar
  },
  data() {
    return {
      heroImage: 'https://readdy.ai/api/search-image?query=Modern%20retail%20shopping%20mall%20interior%20with%20elegant%20design%2C%20soft%20lighting%2C%20clean%20and%20spacious%20environment%2C%20glass%20storefronts%20displaying%20high-quality%20products%2C%20with%20a%20professional%20and%20welcoming%20atmosphere%2C%20perfect%20for%20an%20about%20us%20page%20background&width=1440&height=600&seq=1&orientation=landscape',
      aboutImage: 'https://readdy.ai/api/search-image?query=Modern%20office%20space%20with%20team%20members%20collaborating%2C%20bright%20and%20airy%20environment%20with%20glass%20walls%2C%20plants%2C%20and%20contemporary%20furniture%2C%20professional%20business%20setting%20with%20diverse%20team%20discussing%20ideas%20around%20a%20table&width=600&height=400&seq=2&orientation=landscape',
      timelineImage1: 'https://readdy.ai/api/search-image?query=Small%20online%20shop%20startup%20with%20entrepreneurs%20working%20on%20laptops%20in%20a%20cozy%20office%20space%2C%20planning%20their%20e-commerce%20business%2C%20with%20product%20samples%20displayed%20on%20shelves%2C%20warm%20lighting%20and%20modern%20minimal%20design&width=600&height=400&seq=3&orientation=landscape',
      timelineImage2: 'https://readdy.ai/api/search-image?query=Growing%20e-commerce%20business%20with%20expanded%20product%20range%20including%20fashion%2C%20beauty%20and%20electronics%20displayed%20in%20a%20modern%20showroom%2C%20staff%20arranging%20products%2C%20professional%20photography%20setup%20for%20product%20images&width=600&height=400&seq=4&orientation=landscape',
      timelineImage3: 'https://readdy.ai/api/search-image?query=Tech%20team%20working%20on%20e-commerce%20platform%20development%2C%20software%20engineers%20at%20computers%20with%20multiple%20screens%20showing%20code%20and%20user%20interface%20designs%2C%20modern%20tech%20office%20with%20digital%20displays%20showing%20analytics&width=600&height=400&seq=5&orientation=landscape',
      timelineImage4: 'https://readdy.ai/api/search-image?query=Large%20modern%20warehouse%20with%20organized%20shelving%20systems%2C%20workers%20fulfilling%20orders%2C%20automated%20sorting%20equipment%2C%20shipping%20area%20with%20packages%20ready%20for%20delivery%2C%20clean%20and%20efficient%20logistics%20operation&width=600&height=400&seq=6&orientation=landscape',
      timelineImage5: 'https://readdy.ai/api/search-image?query=Contemporary%20corporate%20headquarters%20with%20impressive%20glass%20facade%2C%20busy%20reception%20area%20with%20company%20logo%20displayed%20prominently%2C%20professional%20staff%20in%20modern%20office%20environment%2C%20meeting%20rooms%20visible%20through%20glass%20walls&width=600&height=400&seq=7&orientation=landscape',
      teamMembers: [
        {
          name: '周宇',
          position: '创始人兼CEO',
          description: '拥有15年互联网行业经验，曾任职于多家知名电商平台，2018年创立新锋未来，致力于打造高品质电商平台。',
          image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20male%20CEO%20in%20his%2040s%20wearing%20a%20business%20suit%2C%20confident%20expression%2C%20modern%20office%20background%2C%20corporate%20portrait%20with%20neutral%20lighting%2C%20clean%20professional%20headshot%20for%20company%20about%20us%20page&width=400&height=500&seq=8&orientation=portrait'
        },
        {
          name: '李婷',
          position: '首席运营官',
          description: '曾任国际知名咨询公司高级顾问，拥有丰富的零售运营和供应链管理经验，负责玲珑ai定制商城的整体运营策略。',
          image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20female%20COO%20in%20her%2030s%20wearing%20business%20attire%2C%20confident%20smile%2C%20modern%20office%20background%2C%20corporate%20portrait%20with%20neutral%20lighting%2C%20clean%20professional%20headshot%20for%20company%20about%20us%20page&width=400&height=500&seq=9&orientation=portrait'
        },
        {
          name: '王强',
          position: '技术总监',
          description: '计算机科学博士，曾在硅谷顶尖科技公司工作，拥有丰富的电商平台架构和AI算法开发经验。',
          image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20male%20CTO%20in%20his%2030s%20wearing%20casual%20business%20attire%2C%20glasses%2C%20thoughtful%20expression%2C%20tech%20office%20background%20with%20computers%2C%20corporate%20portrait%20with%20neutral%20lighting%2C%20clean%20professional%20headshot&width=400&height=500&seq=10&orientation=portrait'
        },
        {
          name: '赵雪',
          position: '市场总监',
          description: '拥有10年品牌营销经验，曾服务多家知名品牌，擅长数字营销和品牌策略，负责玲珑ai定制商城的品牌建设和市场推广。',
          image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20female%20marketing%20director%20in%20her%2030s%20wearing%20stylish%20business%20attire%2C%20creative%20confident%20expression%2C%20modern%20office%20background%2C%20corporate%20portrait%20with%20neutral%20lighting%2C%20clean%20professional%20headshot&width=400&height=500&seq=11&orientation=portrait'
        }
      ],
      contactForm: {
        name: '',
        email: '',
        subject: '',
        message: ''
      }
    }
  },
  methods: {
    submitForm() {
      alert('感谢您的留言，我们会尽快回复！');
      this.contactForm = {
        name: '',
        email: '',
        subject: '',
        message: ''
      };
    }
  }
}
</script>

<style scoped>
.about-us-page {
  font-family: 'Helvetica Neue', Arial, sans-serif;
}

.el-input__inner, .el-textarea__inner {
  border: 1px solid #e2e8f0 !important;
  border-radius: 0.5rem !important;
}

.el-select {
  width: 100%;
}
</style> 